import { useState } from "react";
import { format, parseISO } from "date-fns";
import { nb } from "date-fns/locale";
import { formatHours } from "@/lib/format";
import type { DayOverview } from "@/hooks/use-month-time-overview";
import type { TimeGap } from "@/hooks/use-time-gaps";
import CloseMonthDayRow from "./CloseMonthDayRow";
import { getDayStatus, groupByIsoWeek } from "./day-status";

interface CloseMonthDayListProps {
  days: DayOverview[];
  gaps: TimeGap[];
  onAddEntry?: (date: string) => void;
}

/** «12.–16. okt.» for dagene i en uke. */
function weekRange(days: DayOverview[]): string {
  const first = parseISO(days[0].date);
  const last = parseISO(days[days.length - 1].date);
  if (days.length === 1) return format(first, "d. MMM", { locale: nb });
  return `${format(first, "d.", { locale: nb })}–${format(last, "d. MMM", { locale: nb })}`;
}

export default function CloseMonthDayList({
  days,
  gaps,
  onAddEntry,
}: CloseMonthDayListProps) {
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const gapMap = new Map(gaps.map((g) => [g.date, g]));

  // VIB-3034: alle tidligere ukedager vises, også de uten timer, så hullene
  // synes. Helger vises bare når det er ført på dem. Framtidige dager utelates.
  const visibleDays = days.filter((day) => {
    const gap = gapMap.get(day.date);
    if (!gap) return day.entries.length > 0;
    const status = getDayStatus(gap);
    if (status === "future") return false;
    if (status === "weekend") return day.entries.length > 0;
    return true;
  });

  const toggleDay = (date: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  return (
    <div className="space-y-1.5">
      {groupByIsoWeek(visibleDays).map(({ week, items }) => {
        const logged = items.reduce((sum, d) => sum + d.totalHours, 0);
        const capacity = items.reduce((sum, d) => sum + (gapMap.get(d.date)?.expectedHours ?? 0), 0);
        return (
          <div key={week} className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-2 pt-4 pb-1">
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Uke {week} · {weekRange(items)}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {formatHours(logged)} av {formatHours(capacity)}
              </span>
            </div>
            {items.map((day) => (
              <CloseMonthDayRow
                key={day.date}
                day={day}
                gap={gapMap.get(day.date)}
                expanded={expandedDays.has(day.date)}
                onToggle={() => toggleDay(day.date)}
                onAddEntry={onAddEntry}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
